import {
  ComponentMultiStyleConfig,
  Input,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../lib/styles/props/component-style-props";

export type CMFormInputBaseTextProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  variant?: "default" | "error" | "diff";
  size?: "sm" | "md" | "lg";

  placeholder?: string;

  /**
   * 値。
   */
  value?: string | null;

  /**
   * 差分モード時の変更前の値。
   * 差分モードの場合のみ必須。
   */
  changedFrom?: string | null;

  onChange?: (value: string) => void;
  onBlur?: () => void;
} & ComponentStyleProps;

/**
 * テキストインプットを表示するコンポーネント
 */
export const CMFormInputBaseText = ({
  editMode = "editable",
  variant = "default",
  size = "md",

  placeholder,
  value = null,

  changedFrom = null,

  onChange,
  onBlur,

  sx,
  ...rest
}: CMFormInputBaseTextProps) => {
  const styles = useMultiStyleConfig("CMFormInputBaseTextStyles", {
    size,
  });
  const isDiffMode = useMemo(() => variant === "diff", [variant]);

  const disabled = useMemo(
    () => editMode === "disabled" || editMode === "readOnly",
    [editMode],
  );
  const isInvalid = useMemo(() => variant === "error", [variant]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      onChange?.(value);
    },
    [onChange],
  );

  /**
   * 差分モード時、値が変更前と異なるならtrue。
   * 以下のいずれかの場合はfalse。
   * - 差分モードではない。
   * - 変更前と値が同一である。
   */
  const hasChangedInDiffMode = useMemo(
    () => isDiffMode && value !== changedFrom,
    [isDiffMode, value, changedFrom],
  );

  const inputStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );
  const readonlyStyles = useMemo(
    () => ({
      ...styles.readonly,
      ...(hasChangedInDiffMode ? styles.diff : undefined),
      ...sx,
    }),
    [styles, hasChangedInDiffMode, sx],
  );

  return editMode === "readOnly" ? (
    <Text sx={readonlyStyles} {...rest}>
      {value}
    </Text>
  ) : (
    <Input
      disabled={disabled}
      isInvalid={isInvalid}
      size={size}
      placeholder={placeholder}
      value={value ?? ""}
      onChange={handleChange}
      onBlur={onBlur}
      sx={inputStyles}
      {...rest}
    />
  );
};

export const CMFormInputBaseTextStyles: ComponentMultiStyleConfig = {
  parts: ["containerBase", "readonly", "diff"],
  baseStyle: {
    containerBase: {
      bgColor: "white",
      _placeholder: {
        color: "blackAlpha.500",
      },
    },
    readonly: {
      maxW: "max-content",
    },
    diff: {
      borderWidth: "1px",
      borderColor: "orange.500",
      _hover: {
        borderColor: "orange.500",
      },
      _focus: {
        _hover: {
          borderColor: "blue.500",
        },
      },
    },
  },
  sizes: {
    sm: {
      containerBase: {
        px: "6px",
      },
    },
    md: {
      containerBase: {
        px: "8px",
      },
    },
    lg: {
      containerBase: {
        px: "10px",
      },
    },
  },
};
